import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, MapPin, Loader } from 'lucide-react';
import { useSnackbar } from '../../contexts/SnackbarContext';
import api from '../../utils/api';
import { LOCATIONS, getCountryFromState, isDistrictInList, OTHER_DISTRICT_LABEL } from '../../utils/Locations';
import './AddMosque.scss';

const EditMosque = () => {
    const { mosqueID } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const returnTo = location.state?.returnTo || `/mosques/${mosqueID}`;
    const { success, error } = useSnackbar();
    const [formData, setFormData] = useState({
        name: '',
        country: 'Kerala',
        district: '',
        state: 'Kerala',
        customCountry: '',
        customDistrict: '',
        dish: '',
        googleMapsLink: '',
        location: {
            latitude: '',
            longitude: ''
        },
        description: '',
        keywords: [],
        status: 'available',
        verified: false
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [extractingCoords, setExtractingCoords] = useState(false);

    const dishes = ['Biriyani', 'Neychoru', 'Pathiri', 'Snacks', 'Jeeraka Kanji ജീരക കഞ്ഞി', 'Other'];

    useEffect(() => {
        fetchMosque();
    }, [mosqueID]);

    const fetchMosque = async () => {
        try {
            const response = await api.get(`/admin/mosques/${mosqueID}`);
            if (response.data.success) {
                const mosque = response.data.mosque;
                const state = mosque.state || 'Kerala';
                const district = mosque.district || '';
                const country = getCountryFromState(state);
                const districtInList = isDistrictInList(country, district, state);
                const useOtherCountry = country === 'Other';
                const useOtherDistrict = !useOtherCountry && !districtInList && district;

                setFormData({
                    name: mosque.name || '',
                    country: useOtherCountry ? 'Other' : country,
                    district: useOtherDistrict ? OTHER_DISTRICT_LABEL : district,
                    customCountry: useOtherCountry ? state : '',
                    customDistrict: useOtherDistrict ? district : '',
                    state,
                    dish: mosque.dish || '',
                    googleMapsLink: mosque.googleMapsLink || '',
                    location: mosque.location || { latitude: '', longitude: '' },
                    description: mosque.description || '',
                    keywords: mosque.keywords || [],
                    status: mosque.status || 'available',
                    verified: mosque.verified || false
                });
            }
        } catch (err) {
            error('Error loading mosque');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (name === 'googleMapsLink') {
            setFormData(prev => ({
                ...prev,
                googleMapsLink: value
            }));
        } else if (name.includes('location.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                location: {
                    ...prev.location,
                    [field]: value
                }
            }));
        } else if (name === 'keywords') {
            setFormData(prev => ({
                ...prev,
                keywords: value.split(',').map(k => k.trim()).filter(k => k)
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const handleExtractCoordinates = async () => {
        const link = formData.googleMapsLink?.trim();
        
        if (!link || link === '') {
            error('Please enter a Google Maps link first');
            return;
        }

        if (!link.startsWith('http://') && !link.startsWith('https://')) {
            error('Please enter a valid URL starting with http:// or https://');
            return;
        }

        setExtractingCoords(true);
        try {
            const response = await api.post('/admin/mosques/extract-coordinates', {
                googleMapsLink: link
            });
            
            if (response.data.success && response.data.coordinates) {
                setFormData(prev => ({
                    ...prev,
                    location: {
                        latitude: response.data.coordinates.latitude,
                        longitude: response.data.coordinates.longitude
                    }
                }));
                success(`Coordinates extracted: ${response.data.coordinates.latitude.toFixed(6)}, ${response.data.coordinates.longitude.toFixed(6)}`);
            } else {
                error('Could not extract coordinates from the URL. Please check the link format.');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Error extracting coordinates';
            error(errorMessage);
            console.error('Extract coordinates error:', err.response?.data || err);
        } finally {
            setExtractingCoords(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        // Resolve district and state for API
        let district = formData.district;
        let state = formData.state;
        if (formData.country === 'Other') {
            district = formData.customDistrict?.trim();
            state = formData.customCountry?.trim();
        } else if (formData.country === 'India') {
            state = formData.state?.trim();
            district = formData.district === OTHER_DISTRICT_LABEL ? formData.customDistrict?.trim() : formData.district;
        } else if (formData.district === OTHER_DISTRICT_LABEL) {
            district = formData.customDistrict?.trim();
            state = LOCATIONS[formData.country]?.name || formData.country;
        } else {
            state = LOCATIONS[formData.country]?.name || formData.country;
        }

        const payload = {
            ...formData,
            district,
            state
        };
        delete payload.country;
        delete payload.customCountry;
        delete payload.customDistrict;

        try {
            const response = await api.put(`/admin/mosques/${mosqueID}`, payload);
            if (response.data.success) {
                success('Mosque updated successfully!');
                navigate(returnTo);
            }
        } catch (err) {
            error(err.response?.data?.message || 'Error updating mosque');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="loading">Loading mosque...</div>;
    }

    return (
        <div className="add-mosque">
            <div className="page-header">
                <button className="back-button" onClick={() => navigate(returnTo)}>
                    <ArrowLeft size={20} />
                    Back
                </button>
                <h2>Edit Mosque</h2>
            </div>

            <form className="mosque-form" onSubmit={handleSubmit}>

                <div className="form-row">
                    <div className="form-group">
                        <label>Mosque Name *</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Country / Region *</label>
                        <select
                            name="country"
                            value={formData.country}
                            onChange={(e) => {
                                const v = e.target.value;
                                const loc = LOCATIONS[v];
                                setFormData(prev => ({
                                    ...prev,
                                    country: v,
                                    district: '',
                                    customCountry: v === 'Other' ? prev.customCountry : '',
                                    customDistrict: '',
                                    state: v === 'India' ? '' : (v === 'Other' ? prev.customCountry : (loc?.name || v))
                                }));
                            }}
                            required
                        >
                            {Object.keys(LOCATIONS).map(k => (
                                <option key={k} value={k}>{LOCATIONS[k].name}</option>
                            ))}
                        </select>
                    </div>

                    {formData.country === 'Other' ? (
                        <>
                            <div className="form-group">
                                <label>Country or Region (e.g. Pakistan, Sri Lanka) *</label>
                                <input
                                    type="text"
                                    name="customCountry"
                                    value={formData.customCountry}
                                    onChange={handleChange}
                                    placeholder="Enter country or region"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>District or City *</label>
                                <input
                                    type="text"
                                    name="customDistrict"
                                    value={formData.customDistrict}
                                    onChange={handleChange}
                                    placeholder="Enter district or city"
                                    required
                                />
                            </div>
                        </>
                    ) : formData.country === 'India' ? (
                        <>
                            <div className="form-group">
                                <label>State *</label>
                                <select
                                    name="state"
                                    value={formData.state}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setFormData(prev => ({ ...prev, state: v, district: '', customDistrict: '' }));
                                    }}
                                    required
                                >
                                    <option value="">Select State</option>
                                    {Object.keys(LOCATIONS.India?.states || {}).map(s => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>City *</label>
                                <select
                                    name="district"
                                    value={formData.district === OTHER_DISTRICT_LABEL ? (formData.customDistrict ? OTHER_DISTRICT_LABEL : formData.district) : formData.district}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setFormData(prev => ({
                                            ...prev,
                                            district: v,
                                            customDistrict: v === OTHER_DISTRICT_LABEL ? prev.customDistrict : ''
                                        }));
                                    }}
                                    required
                                >
                                    <option value="">Select City</option>
                                    {(LOCATIONS.India?.states?.[formData.state] || []).map(d => (
                                        <option key={d} value={d}>{d}</option>
                                    ))}
                                    <option value={OTHER_DISTRICT_LABEL}>Other (specify below)</option>
                                </select>
                                {formData.district === OTHER_DISTRICT_LABEL && (
                                    <input
                                        type="text"
                                        name="customDistrict"
                                        value={formData.customDistrict}
                                        onChange={handleChange}
                                        placeholder="Enter city (e.g. Whitefield, Koramangala)"
                                        className="custom-district-input"
                                        required
                                        style={{ marginTop: '0.5rem' }}
                                    />
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="form-group">
                            <label>District / City *</label>
                            <select
                                name="district"
                                value={formData.district === OTHER_DISTRICT_LABEL ? (formData.customDistrict ? OTHER_DISTRICT_LABEL : formData.district) : formData.district}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setFormData(prev => ({
                                        ...prev,
                                        district: v,
                                        customDistrict: v === OTHER_DISTRICT_LABEL ? prev.customDistrict : '',
                                        state: LOCATIONS[prev.country]?.name || prev.country
                                    }));
                                }}
                                required
                            >
                                <option value="">Select District</option>
                                {(LOCATIONS[formData.country]?.districts || []).map(d => (
                                    <option key={d} value={d}>{d}</option>
                                ))}
                                <option value={OTHER_DISTRICT_LABEL}>Other (specify below)</option>
                            </select>
                            {formData.district === OTHER_DISTRICT_LABEL && (
                                <input
                                    type="text"
                                    name="customDistrict"
                                    value={formData.customDistrict}
                                    onChange={handleChange}
                                    placeholder="Enter district or city (e.g. Kondotty, Chennai)"
                                    className="custom-district-input"
                                    required
                                    style={{ marginTop: '0.5rem' }}
                                />
                            )}
                        </div>
                    )}
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Food Type *</label>
                        <select
                            name="dish"
                            value={formData.dish}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Select Food Type</option>
                            {dishes.map(d => (
                                <option key={d} value={d}>{d}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="form-group">
                    <label>Google Maps Link *</label>
                    <div className="input-with-button">
                        <input
                            type="url"
                            name="googleMapsLink"
                            value={formData.googleMapsLink}
                            onChange={handleChange}
                            required
                            placeholder="https://www.google.com/maps/... or https://g.co/kgs/..."
                        />
                        <button
                            type="button"
                            className="extract-coords-btn"
                            onClick={handleExtractCoordinates}
                            disabled={extractingCoords || !formData.googleMapsLink}
                            title="Extract coordinates from URL"
                        >
                            {extractingCoords ? (
                                <Loader size={16} className="spinning" />
                            ) : (
                                <>
                                    <MapPin size={16} />
                                    Extract
                                </>
                            )}
                        </button>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Latitude</label>
                        <input
                            type="number"
                            name="location.latitude"
                            value={formData.location.latitude}
                            onChange={handleChange}
                            step="any"
                        />
                    </div>

                    <div className="form-group">
                        <label>Longitude</label>
                        <input
                            type="number"
                            name="location.longitude"
                            value={formData.location.longitude}
                            onChange={handleChange}
                            step="any"
                        />
                    </div>
                </div>

                <div className="form-group">
                    <label>Description</label>
                    <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows="4"
                    />
                </div>

                <div className="form-group">
                    <label>Keywords (comma-separated)</label>
                    <input
                        type="text"
                        name="keywords"
                        value={formData.keywords.join(', ')}
                        onChange={handleChange}
                    />
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Status</label>
                        <select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                        >
                            <option value="available">Available</option>
                            <option value="unavailable">Unavailable</option>
                            <option value="pending">Pending</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>
                            <input
                                type="checkbox"
                                name="verified"
                                checked={formData.verified}
                                onChange={handleChange}
                            />
                            Verified
                        </label>
                    </div>
                </div>

                <div className="form-actions">
                    <button type="button" onClick={() => navigate(returnTo)}>
                        Cancel
                    </button>
                    <button type="submit" disabled={saving}>
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EditMosque;
